import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import {
  getDefaultAccountExecutiveOptionKeys,
  getDefaultAccountTypeOptions,
  InputType,
  mergeRegisterFormSelectOptions,
  NewInputTextOptions,
  normalizeRegisterFormConfig,
  RegisterFormConfig,
  registerFormEditableSelectFields,
  RegisterFormFieldConfig,
  RegisterFormInputType,
  registerFormInputTypeOptions,
  RegisterFormSection,
  registerFormSectionOptions,
  registerFormSectionTitles,
  sanitizeRegisterFormFieldKey,
} from 'config'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, getLoanSharingUsers, setAdminConfig } from 'services'
import { Button, Modal, SelectOptionInputs } from 'stories/components'
import { ISelectOption } from 'stories/components/SelectOptionInputs/types'
import { confirm, InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

const defaultFieldInputs = (): Record<string, InputType> => ({
  section: {
    inputType: 'select',
    title: 'Section',
    options: registerFormSectionOptions,
    value: 'company',
    required: true,
  },
  title: {
    inputType: 'text',
    title: 'Title',
    value: '',
    required: true,
  },
  inputType: {
    inputType: 'Select',
    title: 'Input Component Type',
    options: registerFormInputTypeOptions,
    value: 'Text',
    required: true,
    hasDefaultOption: true,
  },
  type: {
    inputType: 'select',
    title: 'Input Format',
    options: NewInputTextOptions,
    value: 'text',
    required: true,
    hasDefaultOption: true,
    allowDefaultOption: true,
  },
  options: {
    inputType: 'SelectOptionInput',
    title: 'Select Options',
    value: [],
    required: false,
    visible: false,
  },
  visible: {
    inputType: 'toggle',
    title: 'Visible',
    value: true,
    required: true,
  },
  required: {
    inputType: 'toggle',
    title: 'Required',
    value: false,
    required: true,
  },
})

const getFieldInputs = (field?: RegisterFormFieldConfig) => {
  const inputs = defaultFieldInputs()
  if (!field) return inputs

  inputs.section.value = field.section
  inputs.title.value = field.title
  inputs.inputType.value = field.inputType
  inputs.type.value = field.type || 'text'
  inputs.type.visible = field.inputType === 'Text'
  inputs.options.value = field.options || []
  inputs.options.visible = field.inputType === 'Select'
  inputs.visible.value = field.visible
  inputs.required.value = field.required

  return inputs
}

export const RegisterFormConfiguration = ({ onClose }: { onClose: () => void }) => {
  const [loading, setLoading] = useState(false)
  const [config, setConfig] = useState<RegisterFormConfig>(normalizeRegisterFormConfig())
  const [editField, setEditField] = useState<RegisterFormFieldConfig | null>(null)
  const [isAddingField, setIsAddingField] = useState(false)
  const [fieldInputs, setFieldInputs] = useState(defaultFieldInputs())
  const [editingSelectOptionsField, setEditingSelectOptionsField] = useState<RegisterFormFieldConfig | null>(null)
  const [selectOptionsValue, setSelectOptionsValue] = useState<ISelectOption[]>([])
  const [selectOptionsLoading, setSelectOptionsLoading] = useState(false)

  const groupedFields = useMemo(() => {
    return config.fields.reduce(
      (groups, field) => {
        groups[field.section].push(field)
        return groups
      },
      { company: [], personal: [] } as Record<RegisterFormSection, RegisterFormFieldConfig[]>,
    )
  }, [config])

  useEffect(() => {
    init()
  }, [])

  const init = async () => {
    setLoading(true)
    const data = await getAdminConfig('registerForm')
    setConfig(normalizeRegisterFormConfig(data))
    setLoading(false)
  }

  const updateField = (key: string, values: Partial<RegisterFormFieldConfig>) => {
    const temp = cloneDeep(config)
    const field = temp.fields.find((item) => item.key === key)
    if (!field) return
    Object.assign(field, values)
    setConfig(temp)
  }

  const saveConfig = async (data: RegisterFormConfig = config) => {
    setLoading(true)
    await setAdminConfig('registerForm', data as any)
    setLoading(false)
    toast('Register form configuration saved.', { type: 'success' })
    onClose()
  }

  const onChangeFieldInput = (key: string, value: any) => {
    const temp = cloneDeep(fieldInputs)
    value = InputConvert(temp[key], value)
    temp[key].value = value
    temp[key].error = InputValidate({ ...temp[key], value })
    if (key === 'inputType') {
      temp.type.visible = value === 'Text'
      temp.options.visible = value === 'Select'
      temp.options.required = value === 'Select'
    }
    setFieldInputs(temp)
  }

  const openCustomFieldModal = (field?: RegisterFormFieldConfig) => {
    setEditField(field || null)
    setFieldInputs(getFieldInputs(field))
    setIsAddingField(true)
  }

  const saveCustomField = () => {
    let hasError = false
    const tempInputs = cloneDeep(fieldInputs)
    Object.keys(tempInputs).forEach((key) => {
      const input = tempInputs[key]
      if (input.visible === false) return
      input.error = InputValidate(input)
      if (input.error) hasError = true
    })
    setFieldInputs(tempInputs)
    if (hasError) return

    const inputType = tempInputs.inputType.value as RegisterFormInputType
    const field: RegisterFormFieldConfig = {
      key: editField?.key || sanitizeRegisterFormFieldKey(tempInputs.title.value as string),
      section: tempInputs.section.value as RegisterFormSection,
      title: tempInputs.title.value as string,
      inputType,
      visible: tempInputs.visible.value as boolean,
      required: tempInputs.required.value as boolean,
      custom: true,
      ...(inputType === 'Text' && { type: tempInputs.type.value as RegisterFormFieldConfig['type'] }),
      ...(inputType === 'Select' && { options: tempInputs.options.value as ISelectOption[] }),
    }

    const nextConfig = cloneDeep(config)
    if (!editField && nextConfig.fields.some((item) => item.key === field.key)) {
      field.key = `${field.key}_${Date.now()}`
    }

    const index = nextConfig.fields.findIndex((item) => item.key === field.key)
    if (index === -1) nextConfig.fields.push(field)
    else nextConfig.fields[index] = field

    setConfig(nextConfig)
    setIsAddingField(false)
    setEditField(null)
  }

  const deleteCustomField = async (field: RegisterFormFieldConfig) => {
    const result = await confirm(`Are you sure to remove ${field.title}?`)
    if (!result) return

    const nextConfig = {
      fields: config.fields.filter((item) => item.key !== field.key),
    }
    setConfig(nextConfig)
  }

  const openSelectOptionsModal = async (field: RegisterFormFieldConfig) => {
    setSelectOptionsLoading(true)
    setEditingSelectOptionsField(field)

    let defaultOptions: ISelectOption[] = []
    if (field.key === 'accountType') {
      defaultOptions = getDefaultAccountTypeOptions()
    } else if (field.key === 'accountExecutive') {
      const sharingUsers = await getLoanSharingUsers()
      defaultOptions = [
        ...getDefaultAccountExecutiveOptionKeys(),
        ...Object.entries(sharingUsers).map(([key, title]) => ({
          key,
          title: title as string,
          visible: true,
          isCustom: false,
        })),
      ]
    }

    setSelectOptionsValue(mergeRegisterFormSelectOptions(field.options, defaultOptions))
    setSelectOptionsLoading(false)
  }

  const saveSelectOptions = () => {
    if (!editingSelectOptionsField) return
    updateField(editingSelectOptionsField.key, { options: selectOptionsValue })
    setEditingSelectOptionsField(null)
  }

  const renderField = (field: RegisterFormFieldConfig, index: number) => {
    return (
      <div
        className={`flex items-center justify-between gap-3 px-2 py-2 ${index % 2 ? 'bg-slate-100' : ''}`}
        key={field.key}
      >
        <div>
          <p>{field.title}</p>
          <p className="text-xs text-gray-400">{field.custom ? field.inputType : 'Default field'}</p>
        </div>
        <div className="flex items-center gap-3">
          <RenderInput
            input={{ inputType: 'toggle', title: 'Visible', value: field.visible }}
            Key={`${field.key}-visible`}
            onChange={(_: string, value: boolean) => updateField(field.key, { visible: value })}
          />
          <RenderInput
            input={{ inputType: 'toggle', title: 'Required', value: field.required }}
            Key={`${field.key}-required`}
            onChange={(_: string, value: boolean) => updateField(field.key, { required: value })}
          />
          {field.custom && (
            <>
              <span className="btn-icon cursor-pointer" onClick={() => openCustomFieldModal(field)}>
                <PencilSquareIcon className="w-4 h-4" />
              </span>
              <span className="btn-icon !text-red-400 cursor-pointer" onClick={() => deleteCustomField(field)}>
                <TrashIcon className="w-4 h-4" />
              </span>
            </>
          )}
          {registerFormEditableSelectFields.includes(field.key) && (
            <span className="btn-icon cursor-pointer" onClick={() => openSelectOptionsModal(field)}>
              <PencilSquareIcon className="w-4 h-4" />
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <Modal
      title="Update Register Form"
      titleOkay="Save"
      loading={loading}
      isOpen
      onOk={() => saveConfig()}
      onClose={onClose}
    >
      <>
        <div className="w-[760px] max-w-full">
          <div className="flex justify-end">
            <Button color="sky" onClick={() => openCustomFieldModal()} link>
              <span className="flex items-center gap-1">
                <PlusIcon className="w-4 h-4" />
                Add Custom Field
              </span>
            </Button>
          </div>
          {(Object.keys(registerFormSectionTitles) as RegisterFormSection[]).map((section) => (
            <div className="mb-5" key={section}>
              <p className="text-lg font-semibold mb-2">{registerFormSectionTitles[section]}</p>
              <div className="border border-solid border-stone-200 rounded-md overflow-hidden">
                {groupedFields[section].map((field, index) => renderField(field, index))}
              </div>
            </div>
          ))}
        </div>
        {isAddingField && (
          <Modal
            title={editField ? 'Edit Custom Field' : 'Add Custom Field'}
            titleOkay="Save"
            isOpen
            onOk={saveCustomField}
            onClose={() => setIsAddingField(false)}
          >
            <div className="w-96 grid grid-cols-1 gap-4">
              {Object.keys(fieldInputs).map((key) => {
                const input = fieldInputs[key]
                if (input.visible === false) return null
                return (
                  <div className="input" key={key}>
                    <RenderInput input={input} Key={key} onChange={onChangeFieldInput} />
                  </div>
                )
              })}
            </div>
          </Modal>
        )}
        {editingSelectOptionsField && (
          <Modal
            title={`Edit ${editingSelectOptionsField.title} Options`}
            titleOkay="Save"
            loading={selectOptionsLoading}
            isOpen
            onOk={saveSelectOptions}
            onClose={() => setEditingSelectOptionsField(null)}
          >
            <div className="w-[520px] max-w-full">
              <SelectOptionInputs
                title="Select Options"
                value={selectOptionsValue}
                showKey={editingSelectOptionsField.key === 'accountType'}
                onChange={(value: ISelectOption[]) => setSelectOptionsValue(value)}
              />
            </div>
          </Modal>
        )}
      </>
    </Modal>
  )
}
